// swipe3/shared.js — constants, theme tokens & helpers for Swipe Cards 3.0 (Trading Floor)
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DAILY_GOAL_ROI = 20;
// Mission-bar goal — /daily-pnl carries goal.roiPct (the admin's dailyRoiPct
// from floor goal settings, else the 20% house target) with its source.
// DAILY_GOAL_ROI is only the pre-fetch fallback, never a second truth.
export function goalRoiPct(metrics) {
  const v = Number(metrics?.goal?.roiPct);
  return Number.isFinite(v) && v > 0 ? v : DAILY_GOAL_ROI;
}
export function goalIsHouseTarget(metrics) {
  return !(Number(metrics?.goal?.roiPct) > 0) || metrics?.goal?.source === "house";
}
// The zBucks P&L an agent needs to graduate into real-USD trading. The
// backend's shipped ladder (capital_service.REWARD_LADDER[0]) is the source
// of truth: every /floor-chat/agents/zbucks payload carries it in
// summary.gradThreshold, and applyLadderSummary() below updates this live
// binding from it, so the nine consumers of this name follow the backend
// instead of a copy that could drift. 5000 is only the pre-fetch fallback.
export let ZBUCKS_GRAD_THRESHOLD = 5000;
// FloorChat imported this name but nothing ever exported it (it rendered as
// undefined); the ladder's first-rung payout, live from the same summary.
export let ZBUCKS_GRAD_PAYOUT = 100;
export function applyLadderSummary(summary) {
  const t = Number(summary?.gradThreshold);
  const p = Number(summary?.gradPayout);
  if (Number.isFinite(t) && t > 0) ZBUCKS_GRAD_THRESHOLD = t;
  if (Number.isFinite(p) && p > 0) ZBUCKS_GRAD_PAYOUT = p;
}
export const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export { REACTION_EMOJIS, TICKER_SKIP, extractTickers } from "../floor/chatConstants";

// Liquid-glass background art (from design blueprint)
export const BG = {
  dark: "https://images.unsplash.com/photo-1655841439659-0afc60676b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBsaXF1aWQlMjBnbGFzcyUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgzMDE5Mjg2fDA&ixlib=rb-4.1.0&q=85",
  light: "https://images.unsplash.com/photo-1619863182792-ff9b20a5f886?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGxpZ2h0JTIwbGlxdWlkJTIwZ2xhc3MlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MzAxOTI4Nnww&ixlib=rb-4.1.0&q=85",
};

export const THEME = {
  dark: {
    key: "dark",
    bgBase: "#0A0B10",
    scrim: "linear-gradient(160deg, rgba(10,11,16,0.82) 0%, rgba(9,12,22,0.90) 55%, rgba(6,8,14,0.96) 100%)",
    glass: "rgba(255,255,255,0.055)",
    glassStrong: "rgba(255,255,255,0.10)",
    glassBorder: "rgba(255,255,255,0.14)",
    bubble: "rgba(18,22,34,0.86)",
    bubbleUser: "rgba(14,45,92,0.9)",
    card: "#161A28",
    text: "#FFFFFF",
    sub: "rgba(255,255,255,0.64)",
    muted: "rgba(255,255,255,0.40)",
    hair: "rgba(255,255,255,0.08)",
    green: "#00E676", red: "#FF453A", yellow: "#FFD60A", accent: "#0A84FF",
    shadow: "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)",
    drawer: "rgba(9,11,18,0.72)",
    overlay: "rgba(0,0,0,0.62)",
  },
  light: {
    key: "light",
    bgBase: "#EDF1F6",
    scrim: "linear-gradient(160deg, rgba(238,241,246,0.72) 0%, rgba(226,232,240,0.80) 55%, rgba(215,222,232,0.88) 100%)",
    glass: "rgba(255,255,255,0.58)",
    glassStrong: "rgba(255,255,255,0.78)",
    glassBorder: "rgba(255,255,255,0.75)",
    bubble: "rgba(255,255,255,0.9)",
    bubbleUser: "rgba(219,234,254,0.94)",
    card: "#FFFFFF",
    text: "#111827",
    sub: "#475569",
    muted: "#6B7280",
    hair: "rgba(17,24,39,0.08)",
    green: "#00A34D", red: "#E11D48", yellow: "#D97706", accent: "#0056B3",
    shadow: "0 12px 34px rgba(31,41,55,0.14), inset 0 1px 0 rgba(255,255,255,0.85)",
    drawer: "rgba(248,250,252,0.80)",
    overlay: "rgba(18,20,28,0.30)",
  },
};

export const ROOMS = {
  front_office: { label: "Front Office", short: "Front", color: "#94a3b8" },
  options: { label: "Options Pit", short: "Options", color: "#14b8a6" },
  yield: { label: "Yield Desk", short: "Yield", color: "#f59e0b" },
  crypto: { label: "Crypto Vault", short: "Crypto", color: "#f97316" },
};

export const MSG_TONE = {
  chat: { label: "Floor", color: "#94a3b8" },
  trade_alert: { label: "Trade", color: "#4ade80" },
  news: { label: "News", color: "#fbbf24" },
  risk_alert: { label: "Risk", color: "#fb7185" },
  regime: { label: "Regime", color: "#fb923c" },
  user_question: { label: "You", color: "#a78bfa" },
  user_ask_response: { label: "Reply", color: "#38bdf8" },
  fact_check: { label: "Data", color: "#f59e0b" },
};

export function fmtMoney(v, digits = 0) {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v || 0);
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}
export function fmtPct(v, d = 1) {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(d)}%`;
}
// One clock for every UI mode — see lib/marketClock.js.
export { resolveThemeChoice } from "../../lib/marketClock";
// `suffix: true` appends " left" (FloorChat's bare `<strong>{countdown}</strong>`
// prose-style stat row needs the word for context); the default bare digits
// suit a labeled StatChip (Glass UI, Diamond's Drawer) where the label
// alongside it already says "Session".
export function marketCountdown({ suffix = false } = {}) {
  try {
    const et = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day = et.getDay();
    const close = new Date(et); close.setHours(16, 0, 0, 0);
    const open = new Date(et); open.setHours(9, 30, 0, 0);
    if (day === 0 || day === 6 || et >= close) return "Closed";
    if (et < open) return "Pre-market";
    const diff = Math.max(0, close - et);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const clock = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return suffix ? `${clock} left` : clock;
  } catch { return "—"; }
}
// /omega/status carries the model-drift verdict as drift.severity, whose real
// vocabulary (engine.detect_drift) is none | soft | persistent | severe. This
// used to match only red/risk/high and yellow/warn/medium, so a "severe"
// drift — the state in which the backend halts new entries — painted the
// mission card's pill GREEN. Confirmed live during the Diamond UI audit.
export function normalizeRegime(status) {
  const raw = status?.drift?.severity || status?.regime || status?.marketState || "";
  const txt = String(raw).toLowerCase();
  if (!txt) return "";
  if (txt.includes("severe") || txt.includes("red") || txt.includes("risk") || txt.includes("high")) return "RED";
  if (txt.includes("persistent") || txt.includes("soft") || txt.includes("yellow") || txt.includes("warn") || txt.includes("medium")) return "YELLOW";
  return "GREEN";
}
// Auth is cookie-based (credentials: "include" below sends it automatically).
// This now supplies the CSRF header the backend's double-submit-cookie check
// requires on mutating requests — kept under its old name since callers
// across the swipe3/floor components import `tokenHeaders` by that name.
// Frontend (ziprobability.com) and backend (zero22-app.onrender.com) are
// different origins, so document.cookie can never see the csrf_token cookie
// the backend sets — a hard browser same-origin boundary. Login/auth-me
// responses hand the value back in the JSON body instead; App.js stashes it
// in localStorage under this same key (see App.js's CSRF_STORAGE_KEY /
// floorHelpers.js's identical constant) so every surface reads one shared
// value. Fall back to the cookie for same-origin setups (e.g. local dev).
const CSRF_STORAGE_KEY = "roi_csrf_token";

export function tokenHeaders() {
  try {
    const stored = localStorage.getItem(CSRF_STORAGE_KEY);
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  try {
    const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
  } catch { return {}; }
}
export async function fetchJson(path) {
  const res = await fetch(`${API}${path}`, { credentials: "include", headers: tokenHeaders() });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}
export async function postJson(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...tokenHeaders() },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}
export function msgKey(m = {}) {
  const isUser = m.agent === "user" || m.type === "user_question";
  if (isUser) {
    // Optimistic local echo (raw text) + server SSE echo (may carry a leading @handle
    // routing prefix from a thread reply) — strip leading @mentions so both collapse to one.
    // Bucketed by minute (not unbounded) so re-sending the same text later isn't
    // silently dropped — this used to collapse forever on `user:${text}` alone,
    // matching floorHelpers.js's messageDedupeKey before the two were unified.
    const text = String(m.message || "").trim().replace(/^(@[A-Za-z0-9_]+\s+)+/, "").replace(/\s+/g, " ").toLowerCase();
    const minute = m.timestamp ? String(m.timestamp).slice(0, 16) : "";
    return `user:${text}:${minute}`;
  }
  if (m.id) return `id:${m.id}`;
  const minute = m.timestamp ? String(m.timestamp).slice(0, 16) : "";
  const who = m.agent || m.name || "";
  const text = String(m.message || "").trim().replace(/\s+/g, " ").toLowerCase();
  return `${m.type || "chat"}:${who}:${text}:${minute}`;
}
export function glass(T, extra = {}) {
  return {
    background: T.glass,
    backdropFilter: "blur(14px) saturate(1.4)",
    WebkitBackdropFilter: "blur(14px) saturate(1.4)",
    border: `1px solid ${T.glassBorder}`,
    boxShadow: T.shadow,
    borderRadius: 24,
    ...extra,
  };
}


// ── User-mention detection ────────────────────────────────────────────────
// Agents address the desk owner as @<displayName> and shout out their trader-card name.
const _escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const _GENERIC = new Set(["you", "user", "desk owner", "admin", "trader", "member"]);

export function buildUserAliases({ displayName, username, cardName } = {}) {
  const clean = (s) => (s || "").trim().toLowerCase();
  const at = new Set(["you", "me"]);   // only matched when written as @you / @me
  const bare = new Set();              // matched with or without a leading @
  const un = clean(username), dn = clean(displayName), cn = clean(cardName);
  if (un && un !== "user") at.add(un);
  if (dn && !_GENERIC.has(dn)) { at.add(dn.replace(/\s+/g, "")); at.add(dn); }
  if (cn && !_GENERIC.has(cn) && cn.length >= 2) bare.add(cn);
  return { at: [...at], bare: [...bare] };
}

export function mentionsUser(text, aliases) {
  if (!text || !aliases) return false;
  const low = String(text).toLowerCase();
  for (const a of aliases.at) {
    if (a && new RegExp(`@${_escapeRe(a)}\\b`, "i").test(low)) return true;
  }
  for (const b of aliases.bare) {
    if (b && new RegExp(`(^|[^\\w@])@?${_escapeRe(b)}\\b`, "i").test(low)) return true;
  }
  return false;
}
